import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { getModerationOverviewStats, type ModerationOverviewStats } from '../../api/moderation'

function TabLink({
  to,
  end,
  children,
  badge,
  urgentBadge,
  badgeTone = 'neutral',
}: {
  to: string
  end?: boolean
  children: React.ReactNode
  badge?: string | number
  urgentBadge?: string | number
  badgeTone?: 'neutral' | 'urgent'
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold ${
          isActive ? 'border-white/20 bg-white/10 text-white' : 'border-white/10 text-white/70 hover:bg-white/5'
        }`
      }
    >
      <span className="inline-flex items-center gap-2">
        {children}
        {badge !== undefined && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              badgeTone === 'urgent' ? 'bg-rose-500/20 text-rose-200' : 'bg-white/10 text-white/70'
            }`}
          >
            {badge}
          </span>
        )}
        {urgentBadge !== undefined && (
          <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-200">
            urgent {urgentBadge}
          </span>
        )}
      </span>
    </NavLink>
  )
}

export default function SettingsModerationPage() {
  const [stats, setStats] = useState<ModerationOverviewStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const days = 7

  useEffect(() => {
    setLoading(true)
    setError(null)
    getModerationOverviewStats(days)
      .then((data) => setStats(data))
      .catch((err: any) => setError(err?.response?.data?.detail || err?.message || 'Failed to load moderation stats.'))
      .finally(() => setLoading(false))
  }, [days])

  const queueOpenTotal = stats?.queue_open?.total
  const queueOpenUrgent = stats?.queue_open?.urgent

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-extrabold">Moderation</h1>
        <p className="text-sm text-white/70">Review escalations, reports, and appeals.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <TabLink to="/settings/moderation" end>Overview</TabLink>
        <TabLink
          to="/settings/moderation/queue"
          badge={queueOpenTotal}
          urgentBadge={queueOpenUrgent ? queueOpenUrgent : undefined}
          badgeTone={queueOpenUrgent ? 'urgent' : 'neutral'}
        >
          Queue
        </TabLink>
        <TabLink to="/settings/moderation/image-queue">Image Queue</TabLink>
        <TabLink to="/settings/moderation/policies">Policies</TabLink>
      </div>
      <Outlet context={{ stats, loading, error }} />
    </div>
  )
}
